import { GetStaticProps } from 'next';
import Link from 'next/link';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

const CooperativeLearningStudy = () => {
  return (
    <LandingPageLayout
      title="Research Study: Cooperative Learning with Curipod | Curipod"
      subTitle="New research shows Curipod combined with group investigation dramatically boosts mastery and engagement. See how one classroom went from 0% to 89% proficiency."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>RESEARCH STUDY</HeroTag>
            <HeroTitle>
              Cooperative Learning with Curipod Boosts Student Success
            </HeroTitle>
            <HeroMeta>
              <MetaBadge>Middle School</MetaBadge>
              <MetaBadge>Cooperative Learning</MetaBadge>
              <MetaBadge>2025 Study</MetaBadge>
            </HeroMeta>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    21<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Mastery &middot; Traditional Methods</StatLabel>
                </StatItem>
                <ArrowContainer>&rarr;</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    89<StatUnit>%</StatUnit>
                  </StatNumber>
                  <StatLabel>Mastery &middot; With Curipod</StatLabel>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* SUMMARY */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionText>
              <a
                href="https://www.researchgate.net/publication/393038632_Effectiveness_of_Group_Investigation_Cooperative_Learning_Method_Based_on_Curipod_Application_For_Class_II_Students_At_MTs_Uswatun_Hasanah"
                target="_blank"
                rel="noopener noreferrer"
              >
                A 2025 study by Nurhalimah &amp; Bakti Surya
              </a>{' '}
              found that pairing Curipod with a cooperative, inquiry-based learning model
              led to significant gains in mastery and engagement. In a middle school
              religious studies class, student mastery rose from 0% to 89% using the Group
              Investigation method with Curipod &mdash; compared to just 21% with
              traditional methods.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INTRODUCTION */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Introduction</SectionTitle>
            <SectionText>
              This{' '}
              <a
                href="https://www.researchgate.net/publication/393038632_Effectiveness_of_Group_Investigation_Cooperative_Learning_Method_Based_on_Curipod_Application_For_Class_II_Students_At_MTs_Uswatun_Hasanah"
                target="_blank"
                rel="noopener noreferrer"
              >
                2025 classroom study
              </a>{' '}
              explored how Curipod enhanced the Group Investigation (GI) cooperative
              learning model. It followed 39 middle-school students in an Islamic
              Religious Education class, tracking learning outcomes over two instructional
              cycles. Results were striking &mdash; combining Curipod&apos;s interactive
              platform with group inquiry significantly boosted mastery and engagement.
            </SectionText>

            <SectionTitle>
              From 0% to 89% Mastery &ndash; A Dramatic Improvement
            </SectionTitle>
            <SectionText>
              Before using the Curipod-based Group Investigation approach, no students met
              mastery (average pre-test score: 36.6%). Following one cycle of traditional
              teaching, just 21.4% of students reached the proficiency benchmark. When the
              teacher introduced Group Investigation with Curipod in the next cycle, the
              gains were dramatic.{' '}
              <strong>
                Nearly 90% of students (89.48%) achieved the mastery criteria
              </strong>{' '}
              in that unit. Though conducted in an Islamic Studies class, the Group
              Investigation method is highly versatile, encouraging students to work in
              teams to investigate topics and share insights.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* ENGAGING LEARNERS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Engaging Learners Through Interaction and Inquiry</SectionTitle>
            <SectionText>
              Beyond test scores, the Curipod-enhanced cooperative learning model
              increased{' '}
              <strong>student engagement and sharpened critical thinking</strong>. The
              study found that the Curipod-based GI model &ldquo;significantly improved
              students&apos; engagement, attitudes, and &hellip; stimulated critical
              thinking&rdquo; through interactive content and clear collaborative roles.
              In practice, Curipod allowed each group to contribute meaningfully to a
              shared investigation. This interactive format kept students active and
              invested in learning. The research notes that{' '}
              <strong>
                tools like Curipod provided interactive tasks that sparked deeper analysis
              </strong>
              , rather than passive learning. Students responded positively, saying,
              Curipod&apos;s visuals and interactivity made abstract concepts easier to
              grasp than traditional lectures or textbooks.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* INSPIRING ACHIEVEMENT */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <div>
            <SectionTitle>Inspiring Achievement Through Active Learning</SectionTitle>
            <SectionText>
              This case study offers a blueprint for action: pair collaborative pedagogy
              with AI to boost engagement and outcomes. Curriculum directors might
              consider providing teachers with professional development on cooperative
              learning strategies (like Group Investigation) alongside training in
              Curipod&apos;s features. Researchers describe this approach as GI-Curipod,
              using the platform at every phase&mdash;from topic exploration to group
              research to presenting findings.
            </SectionText>
            <SectionText>
              Ultimately, the Curipod-fueled Group Investigation model aligns with a
              vision of <strong>student-centered, active learning</strong>. It shows that
              when students actively investigate knowledge rather than passively receive
              it, their success soars. And importantly, Curipod makes facilitating this
              kind of learning more feasible. Study analysis noted the GI-Curipod model
              significantly boosted engagement and attitudes, with structured tasks that
              &lsquo;stimulated critical thinking&rsquo;&mdash;a sign students enjoyed the
              process. This suggests that such methods not only teach facts, but teach
              students <em>how to think</em>, an outcome any education leader values.
            </SectionText>
            <SectionText>
              For curriculum directors looking to boost outcomes in core subjects, this
              example from a religious studies class can be extrapolated to science,
              history, or language arts. Wherever content mastery is the goal, consider
              introducing an inquiry-based cycle with Curipod as the interactive backbone.
              The evidence shows you could see both{' '}
              <strong>engagement and mastery metrics rise</strong>. Backed by these
              findings, advocating for interactive AI learning platforms like Curipod
              becomes a strategic move to meet school improvement goals. Investing in
              training and resources positions schools at the forefront of educational
              innovation, where students move beyond learning to truly thriving.
            </SectionText>
          </div>
        </MaxWidthContainer>
      </Section>

      {/* SOURCE & CTA */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <CenteredSection>
            <SourceCard>
              <SourceLabel>SOURCE</SourceLabel>
              <SourceText>
                Nurhalimah, &amp; Bakti, Surya. (2025). Effectiveness of Group
                Investigation Cooperative Learning Method Based on Curipod Application For
                Class II Students At MTs Uswatun Hasanah.{' '}
                <em>
                  International Journal of Islamic Education, Research and
                  Multiculturalism (IJIERM)
                </em>
                . 7. 543&ndash;562. 10.47006/ijierm.v7i2.453.
              </SourceText>
              <a
                href="https://www.researchgate.net/publication/393038632_Effectiveness_of_Group_Investigation_Cooperative_Learning_Method_Based_on_Curipod_Application_For_Class_II_Students_At_MTs_Uswatun_Hasanah"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SourceLink>View on ResearchGate &rarr;</SourceLink>
              </a>
            </SourceCard>

            <GetStartedWrapper>
              <Link href="/signup" passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default CooperativeLearningStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: ${theme.colors.paleBeige};
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 52px;
  color: ${theme.colors.black};
  margin: 0 0 20px;
  line-height: 1.15;
  max-width: 800px;

  @media ${MOBILE} {
    font-size: 36px;
  }

  @media (max-width: 320px) {
    font-size: 28px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const StatBannerWrapper = styled.div`
  background: ${theme.colors.black};
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0;
  padding: 0 20px;

  @media ${MOBILE} {
    gap: 20px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 16px 24px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${({ $highlight }) => ($highlight ? theme.colors.orange : theme.colors.white)};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatUnit = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 26px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  a {
    color: ${theme.colors.orange};
    text-decoration: underline;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── SOURCE & CTA ──────────────────────────────────────────────────────────── */

const SourceCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  padding: 24px 32px;
  box-shadow: ${theme.effects.defaultShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 20px;
  }
`;

const SourceLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  margin-bottom: 8px;
`;

const SourceText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0 0 8px;
`;

const SourceLink = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orange};
  &:hover {
    text-decoration: underline;
  }
`;

const GetStartedWrapper = styled.div`
  margin-top: 8px;
`;
